import { useState, useEffect } from "react";
import Modal from 'react-modal';

import { editProfile } from "helpers/profiles";
import { useLocalisationService } from "providers/LocalisationProvider";
import { useCurrentUserSnapshot } from "hooks/useAccount";
import { useModalScrollLock } from 'hooks/useModalScrollLock';
import { ProfileIcons } from 'fixit-shared/Consts';

Modal.setAppElement('#root');

export const EditProfileModal = ({ isOpen, onClose, profile, onEditedProfile, cloudFunctions }) => {

    useModalScrollLock(isOpen);

    const l = useLocalisationService();
    const [requestInFlight, setRequestInFlight] = useState(false);

    const [displayName, setDisplayName] = useState(profile.displayName);
    const [raceIcons, setRaceIcons] = useState(profile.raceIcons);
    const [genderIcons, setGenderIcons] = useState(profile.genderIcons);
    const [displayNamePulse, setDisplayNamePulse] = useState(false);
    const user = useCurrentUserSnapshot();

    
    useEffect(() => {
        setDisplayName(profile.displayName);
        setRaceIcons(profile.raceIcons);
        setGenderIcons(profile.genderIcons);
    },[isOpen, profile, onClose]);
    
    const isComplete = () => {
        return displayName && raceIcons && genderIcons;
    }

    const renderEditDialogue = () => {
        const confirmStyle = `create-profile-button ${isComplete()?'primary':'disabled'}-button rounded`;

        return (
            <Modal
                className="modal create-profile-modal"
                overlayClassName="modal-overlay create-profile-modal-overlay"
                isOpen={isOpen}
                onRequestClose={closeModal}
                contentLabel="Create Profile"
            >
    
            <form className="modal-flex launch-form" onSubmit={(e)=>e.preventDefault()}> 
                {renderIcons()}
                {renderNameEntry()}
            </form>
            <div className="modal-row">                 
                <button className="create-profile-button secondary-button rounded" onClick={closeModal}>
                    {l.getString("cancel")}
                </button>                
                <button className={confirmStyle} onClick={handleEditProfile}>
                    {l.getString("confirm")}
                </button>
            </div>
          </Modal>);
    }

    const renderInFlightDialogue = () => {
        return <Modal
        className="modal create-profile-modal"
        overlayClassName="modal-overlay create-profile-modal-overlay"
        isOpen={isOpen}
        onRequestClose={closeModal}
        contentLabel="Create Profile">
            <div className="flexbox centered full-height">
                {l.getString('profile_edit_in_flight')}
            </div>
        </Modal>;
    }
    
    const renderNameEntry = () => {
        const style = displayNamePulse ? "rounded pulse-red-inset" : "rounded"
        return (
            <div className="modal-row">
                <div className="modal-flex">
                    {l.getString("profile_create_name_title")}
                    <div className="modal-flex centered">
                        <input className={style} type="text" value={displayName} placeholder={l.getString("profile_create_name_hint")} onChange={handleNameChange} />
                    </div>
                </div>
            </div>
        );
    }

    const renderIcons = () => {
        return (
        <div className="modal-row">
            <div className="modal-flex centered">
                <p> {l.getString("profile_create_icon_title")}</p>
                <div className="flexbox">
                    {renderIcon(ProfileIcons.WhiteMan, "White", "Man")}
                    {renderIcon(ProfileIcons.BlackMan, "Black", "Man")}
                    {renderIcon(ProfileIcons.WhiteWoman, "White", "Woman")}
                    {renderIcon(ProfileIcons.BlackWoman, "Black", "Woman")}
                </div>
            </div>
        </div>);
    }

    const renderIcon = (icon, race, gender) => {
        const textKey = `${race.toLowerCase()}_${gender.toLowerCase()}_option`
        return (
            <img className={iconStyle(race, gender)} src={icon} alt={l.getString(textKey)} onClick={() => handleIconChange(race, gender)}/>
        )
    }

    const resetDisplay = () => {
        setDisplayNamePulse(false);
    }

    const buildProfileObject = () => {
        const id = profile.id;
        // Report indexes are maintained server-side by saveReport, so they are
        // deliberately absent here. compressChanges() only omits a field when it
        // is undefined - sending an empty array would overwrite the real one.
        return {
            id,
            displayName,
            raceIcons,
            genderIcons,
            version: 2
        }
    }

    const handleEditProfile = () => {        
        if(!isComplete()){
            setPulses();
            return;
        }
        setRequestInFlight(true);
        
        editProfile(cloudFunctions, user, buildProfileObject())
        .then(outcome => {
            resetDisplay();
            setRequestInFlight(false);
            onEditedProfile(outcome);
        });
    }

    const setPulses = () => {
        if(!displayName){
            setDisplayNamePulse(true);
        }
        setTimeout( () => {
            setDisplayNamePulse(false);
          }, 1000);
    }

    const iconStyle = (selectedRace, selectedGender) => {
        return (selectedRace === raceIcons && selectedGender === genderIcons) ? "icon-option selected-icon" : "icon-option";
    }

    const handleNameChange = (event) => {
        if(displayNamePulse){
            setDisplayNamePulse(false);
        }
        setDisplayName(event.target.value);
    }

    const handleIconChange = (raceIcons, genderIcons) => {
        setRaceIcons(raceIcons);
        setGenderIcons(genderIcons);
    }

    const closeModal = () => {
        resetDisplay();
        setRequestInFlight(false);
        onClose();
    }

    if(requestInFlight){
        return renderInFlightDialogue();
    }
    else{
        return renderEditDialogue();
    }
}